import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Star, X, Copy, LogOut } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { api, unwrapApiResponse } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { Input } from '@/components/ui/Input';
import { PlanVipTagBadge } from '@/components/plans/PlanVipTagBadge';
import { getPlanTheme } from '@/components/plans/planTheme';
import { PixDiscordPaymentNotice } from '@/components/payment/PixDiscordPaymentNotice';

export default function LandingPage() {
    const navigate = useNavigate();
    const [vipPlans, setVipPlans] = useState<any[]>([]);
    const [plansLoading, setPlansLoading] = useState(true);
    const { isAuthenticated, isLoading: authLoading, refreshTokenAndUpdateUser, logout, user } = useAuth();
    const { requireVipToAccess } = useSiteConfig();
    const hasCheckedPayment = useRef(false);

    // Stats
    const [stats, setStats] = useState<any>(null);
    const [statsLoading, setStatsLoading] = useState(true);

    const [showAllPlans, setShowAllPlans] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showGiftModal, setShowGiftModal] = useState(false);
    const [giftCode, setGiftCode] = useState('');
    const [giftRedeemLoading, setGiftRedeemLoading] = useState(false);
    const [giftRedeemError, setGiftRedeemError] = useState('');
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentData, setPaymentData] = useState<any>(null);
    const [paymentError, setPaymentError] = useState('');

    useEffect(() => {
        fetchVipPlans();
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('obras/stats/public');
            const raw = unwrapApiResponse<any>(response);
            const payload =
                raw && typeof raw === 'object' && typeof raw.total_obras === 'number'
                    ? raw
                    : raw?.data && typeof raw.data === 'object' && typeof raw.data.total_obras === 'number'
                      ? raw.data
                      : null;
            setStats(payload);
        } catch (error) {
            console.error('Erro ao buscar stats:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchVipPlans = async () => {
        try {
            setPlansLoading(true);
            const response = await api.get('planos', { requiresAuth: false });
            const body = (response as any)?.data ?? response;
            const list = Array.isArray(body?.data)
                ? body.data
                : Array.isArray(body)
                  ? body
                  : [];
            setVipPlans(list);
        } catch (error) {
            console.error('Erro ao carregar planos:', error);
            setVipPlans([]);
        } finally {
            setPlansLoading(false);
        }
    };

    // Verificar último pagamento pendente a cada refresh (F5)
    useEffect(() => {
        if (authLoading) return;

        if (isAuthenticated && !hasCheckedPayment.current) {
            hasCheckedPayment.current = true;

            const checkLastPendingPayment = async () => {
                try {
                    const rawList = await api.get('meus-pagamentos', { requiresAuth: true });
                    const body = unwrapApiResponse(rawList) as { items?: unknown[] } | unknown[];
                    const payments = Array.isArray(body)
                        ? body
                        : Array.isArray((body as { items?: unknown[] })?.items)
                          ? (body as { items: unknown[] }).items
                          : [];

                    if (!payments || payments.length === 0) {
                        return;
                    }

                    const pendingPayments = payments.filter((p: any) => p.status === 'pending');

                    if (pendingPayments.length === 0) {
                        return;
                    }

                    const lastPending = pendingPayments[0] as { id?: number };
                    const pid = lastPending?.id;
                    if (typeof pid !== 'number' || pid <= 0) return;

                    try {
                        const resultRaw = await api.post(`meus-pagamentos/${pid}/verificar`, {}, { requiresAuth: true });
                        const result = unwrapApiResponse(resultRaw) as { status?: string };
                        if (result?.status === 'ativado' || result?.status === 'ja_ativado') {
                            await refreshTokenAndUpdateUser();
                        }
                    } catch (error) {
                        console.error('[Landing] Erro ao verificar pagamento:', error);
                    }
                } catch (error) {
                    console.error('[Landing] Erro ao buscar pagamentos:', error);
                }
            };

            checkLastPendingPayment();
        }
    }, [authLoading, isAuthenticated]);

    const formatNumber = (num: number) => {
        if (!num) return '0';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
        return num.toString();
    };

    const features = [
        { text: '+1 mil novels', box: 'bg-amber-500' },
        { text: '+1 mil shoujos', box: 'bg-pink-500' },
        { text: 'Pesquisa em inglês', box: 'bg-blue-500' },
        { text: 'App iOS e Android', box: 'bg-purple-500' },
        { text: 'Download offline', box: 'bg-emerald-500' },
    ];

    const createPaymentLink = async (planId: number) => {
        if (!isAuthenticated) {
            navigate('/entrar');
            return;
        }

        try {
            setPaymentLoading(true);
            setPaymentError('');

            const referrerNick = typeof window !== 'undefined' ? localStorage.getItem('referrerNick') : null;

            const body: any = {};
            if (referrerNick) body.referrerNick = referrerNick;

            const response = await api.post(`planos/${planId}/comprar`, body, { requiresAuth: true });
            const data: any = (response as any)?.data ?? response;

            const checkoutUrl = data?.checkoutUrl ?? data?.redirectUrl;
            if (checkoutUrl) {
                const plan = vipPlans.find((p: any) => p.id === planId);
                setPaymentData({
                    checkoutUrl,
                    redirectUrl: checkoutUrl,
                    reference: data.reference,
                    plan: plan ?? { name: 'VIP', pricing: data?.amount },
                    amount: plan != null ? Number(plan.pricing ?? 0) : Number(data?.amount ?? 0),
                });
                setShowPaymentModal(true);

                if (referrerNick && typeof window !== 'undefined') {
                    localStorage.removeItem('referrerNick');
                }
            } else {
                throw new Error(
                    typeof data?.message === 'string' ? data.message : 'Resposta sem link de pagamento',
                );
            }

        } catch (err: any) {
            console.error('Erro ao criar link de pagamento:', err);
            const msg =
                err?.response?.data?.message ??
                err?.response?.data?.error ??
                err?.message ??
                'Erro ao gerar link de pagamento. Tente novamente.';
            setPaymentError(msg);
            alert(msg);
        } finally {
            setPaymentLoading(false);
        }
    };

    const handleSubscribe = (planId: number) => {
        createPaymentLink(planId);
    };

    const closePaymentModal = () => {
        setShowPaymentModal(false);
        setPaymentData(null);
        setPaymentError('');
    };

    const proceedToPayment = () => {
        if (paymentData?.qrCode) return;
        const url = paymentData?.checkoutUrl ?? paymentData?.redirectUrl;
        if (url) {
            window.open(url, '_blank');
            closePaymentModal();
        }
    };

    const copyPixKey = () => {
        if (paymentData?.pixKey) {
            navigator.clipboard.writeText(paymentData.pixKey);
            alert('Chave PIX copiada!');
        }
    };

    const formatExpirationDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const openGiftModal = () => {
        setShowGiftModal(true);
        setGiftCode('');
        setGiftRedeemError('');
    };

    const closeGiftModal = () => {
        setShowGiftModal(false);
        setGiftCode('');
        setGiftRedeemError('');
        setGiftRedeemLoading(false);
    };

    const handleRedeemGiftCode = async () => {
        const code = giftCode.trim();
        if (!code) {
            setGiftRedeemError('Digite o código.');
            return;
        }
        setGiftRedeemLoading(true);
        setGiftRedeemError('');
        try {
            await api.post('cupons/resgatar', { codigo: code }, { requiresAuth: true });
            await refreshTokenAndUpdateUser();
            closeGiftModal();
            navigate('/inicio');
        } catch (err: any) {
            const msg = err?.message || err?.response?.data?.message || 'Não foi possível resgatar. Verifique o código e tente novamente.';
            setGiftRedeemError(msg);
        } finally {
            setGiftRedeemLoading(false);
        }
    };

    if (authLoading) {
        return (
            <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    return (
        <main className="min-h-screen bg-neutral-950 text-white relative overflow-hidden">
            {/* Hero Section */}
            <section className="min-h-screen flex flex-col items-center justify-center px-6 relative">
                {/* Imagens decorativas de fundo */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                    {/* Wave — canto superior esquerdo */}
                    <div
                        className="absolute -top-24 -left-24 w-96 h-96 md:w-[600px] md:h-[600px] opacity-20"
                        style={{
                            maskImage: 'radial-gradient(ellipse 100% 100% at 0% 0%, black 50%, transparent 85%)',
                            WebkitMaskImage: 'radial-gradient(ellipse 100% 100% at 0% 0%, black 50%, transparent 85%)'
                        }}
                    >
                        <img
                            src="/wave2.png"
                            alt=""
                            width={600}
                            height={600}
                            className="object-contain"
                        />
                    </div>
                    {/* Urso — colado na direita, opacidade baixa, gradient vindo da esquerda pra parede (direita) */}
                    <div
                        className="absolute bottom-0 -right-16 sm:-right-24 md:-right-32 w-[420px] h-[420px] sm:w-[550px] sm:h-[550px] md:w-[700px] md:h-[700px] lg:w-[900px] lg:h-[900px] opacity-[0.05]"
                        style={{
                            maskImage: 'linear-gradient(to right, transparent 25%, black 80%)',
                            WebkitMaskImage: 'linear-gradient(to right, transparent 25%, black 80%)'
                        }}
                    >
                        <img
                            src="/urso.webp"
                            alt=""
                            width={900}
                            height={900}
                            className="object-contain object-right-bottom"
                        />
                    </div>
                </div>

                <div className="max-w-4xl mx-auto text-center z-10 text-white">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl sm:text-5xl md:text-7xl font-black mb-4 leading-tight text-white"
                    >
                        A melhor experiência para{' '}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-red-600">
                            Leitura
                        </span>
                    </motion.h1>
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="flex justify-center mb-6">
                        <span className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-semibold tracking-wider">
                            <span className="animate-blink h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]" />
                            Atualizado 24h
                        </span>
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mb-10"
                    >
                        Acesso ilimitado, leitura offline e o maior catálogo de obras atualizado 24h por dia.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4 w-full max-w-md mx-auto sm:max-w-none sm:w-auto px-1"
                    >
                        {isAuthenticated ? (
                            <>
                                <Link
                                    to={requireVipToAccess && !user?.plan?.isActive ? "/meu-perfil" : "/inicio"}
                                    className="px-10 py-4 bg-orange-500 text-white font-bold rounded-full transition-all hover:scale-[1.02] sm:hover:scale-105 active:scale-95 shadow-lg shadow-orange-500/20 text-center w-full sm:w-auto min-h-[52px] flex items-center justify-center"
                                >
                                    {requireVipToAccess && !user?.plan?.isActive ? "Ir para o Perfil" : "Ir para o Início"}
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => logout()}
                                    className="flex w-full sm:w-auto min-h-[52px] shrink-0 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-8 py-4 text-sm font-semibold text-white/90 shadow-sm transition-colors hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-200 active:scale-[0.98]"
                                >
                                    <LogOut size={18} className="opacity-90" aria-hidden />
                                    Sair
                                </button>
                            </>
                        ) : (
                            <Link
                                to="/entrar"
                                className="px-10 py-4 bg-white text-black font-bold rounded-full transition-all hover:scale-105 active:scale-95 shadow-xl text-center"
                            >
                                Começar Agora
                            </Link>
                        )}
                    </motion.div>
                </div>

                {/* Bloco único: stats + benefícios */}
                <div className="relative w-full px-6 mt-10">
                    <div className="max-w-2xl mx-auto flex flex-col gap-6">
                        {/* Stats: Obras + Capítulos — barra única com divisor */}
                        <div className="flex justify-center">
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.28 }}
                                className="inline-flex items-center gap-0 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-4"
                            >
                                <div className="flex items-baseline gap-2 pr-6">
                                    <span className="text-2xl sm:text-3xl font-black tabular-nums bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-red-600">
                                        {statsLoading ? '...' : formatNumber(stats?.total_obras)}
                                    </span>
                                    <span className="text-xs text-white/50 uppercase tracking-widest font-medium">Obras</span>
                                </div>
                                <div className="w-px h-8 bg-white/15" aria-hidden="true" />
                                <div className="flex items-baseline gap-2 pl-6">
                                    <span className="text-2xl sm:text-3xl font-black tabular-nums bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-red-600">
                                        {statsLoading ? '...' : formatNumber(stats?.total_capitulos)}
                                    </span>
                                    <span className="text-xs text-white/50 uppercase tracking-widest font-medium">Capítulos</span>
                                </div>
                            </motion.div>
                        </div>

                        <div className="pt-2 border-t border-white/[0.06] space-y-2 sm:space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 pt-2">
                                {features.slice(0, 3).map((feature, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.05 * i }}
                                        className="flex items-center gap-4 py-4 px-4 sm:py-2.5 sm:px-3 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.06] hover:border-white/[0.08] hover:scale-[1.02] sm:hover:scale-105 hover:shadow-lg hover:shadow-black/20 transition-all duration-200 cursor-default"
                                    >
                                        <div className={`shrink-0 w-8 h-8 sm:w-6 sm:h-6 rounded-lg flex items-center justify-center relative overflow-hidden ${feature.box}`}>
                                            <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />
                                            <Check size={18} className="text-white relative z-10 strokeWidth={2.5}" />
                                        </div>
                                        <span className="text-white/90 text-sm sm:text-sm font-medium">{feature.text}</span>
                                    </motion.div>
                                ))}
                            </div>
                            <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:justify-center gap-2 sm:gap-3">
                                {features.slice(3, 5).map((feature, i) => (
                                    <motion.div
                                        key={i + 3}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.15 + i * 0.05 }}
                                        className="flex items-center gap-4 py-4 px-4 sm:py-2.5 sm:px-3 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.06] hover:border-white/[0.08] hover:scale-[1.02] sm:hover:scale-105 hover:shadow-lg hover:shadow-black/20 transition-all duration-200 cursor-default"
                                    >
                                        <div className={`shrink-0 w-8 h-8 sm:w-6 sm:h-6 rounded-lg flex items-center justify-center relative overflow-hidden ${feature.box}`}>
                                            <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />
                                            <Check size={18} className="text-white relative z-10 strokeWidth={2.5}" />
                                        </div>
                                        <span className="text-white/90 text-sm font-medium">{feature.text}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Wave cinza separando hero da seção de planos — tom escuro */}
            <div className="relative w-full h-10 sm:h-14 overflow-hidden -mb-1" aria-hidden="true">
                <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                    <path d="M0 50 Q 180 0 360 50 Q 540 100 720 50 Q 900 0 1080 50 Q 1260 100 1440 50 L1440 80 L0 80 Z" fill="rgb(10 10 10)" />
                </svg>
            </div>

            {/* Plans Section — gradient de cima pra baixo */}
            <section id="planos" className="pt-6 pb-16 md:pt-8 md:pb-24 px-6 bg-gradient-to-b from-neutral-950 to-neutral-900">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-black text-center mb-8">
                        Escolha seu{' '}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-red-600">
                            Plano
                        </span>
                    </h2>

                    {plansLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                {(() => {
                                    let plans = showAllPlans
                                        ? [...vipPlans]
                                        : vipPlans.filter((p: any) => !String(p.name || '').toLowerCase().includes('rei'));
                                    const anualIndex = plans.findIndex((p: any) => String(p.name || '').toLowerCase().includes('anual'));
                                    if (anualIndex > 0 && plans.length === 3) {
                                        [plans[1], plans[anualIndex]] = [plans[anualIndex], plans[1]];
                                    }
                                    return plans;
                                })().map((plan: any) => {
                                    const theme = getPlanTheme(plan.name ?? '');
                                    return (
                                    <div
                                        key={plan.id}
                                        className={`relative rounded-xl border p-4 transition-all flex flex-col overflow-hidden ${theme.border} ${theme.bg} ${theme.hoverBorder} ${theme.hoverBg}`}
                                    >
                                        {/* Linha decorativa no topo */}
                                        <div className={`absolute top-0 left-0 right-0 h-[2px] ${theme.topLine}`} />

                                        {String(plan.name || '').toLowerCase().includes('experiente') && (
                                            <div className="absolute top-0 right-0 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-bl-lg rounded-tr-xl flex items-center gap-0.5">
                                                <Star size={8} fill="currentColor" /> TOP
                                            </div>
                                        )}

                                        <h3 className={`text-sm font-bold mb-1 pr-8 leading-tight ${theme.nameColor}`}>{plan.name}</h3>
                                        <PlanVipTagBadge plan={plan} size="sm" className="mb-1.5" />
                                        <div className="text-base font-bold text-white mb-1">
                                            R$ {Number(plan.pricing ?? 0).toFixed(2).replace('.', ',')}
                                        </div>
                                        <div className="text-xs text-white/40 mb-3">{plan.validityDays ?? 0} dias</div>

                                        <p className="text-white/50 text-xs mb-4 flex-1 leading-relaxed">{plan.description || ''}</p>

                                        <button
                                            type="button"
                                            onClick={() => handleSubscribe(plan.id)}
                                            disabled={paymentLoading}
                                            className={`w-full py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${theme.buttonBase} ${theme.buttonHover}`}
                                        >
                                            {paymentLoading ? 'Processando...' : 'Assinar'}
                                        </button>
                                    </div>
                                    );
                                })}
                            </div>

                            {vipPlans.some((p: any) => String(p.name || '').toLowerCase().includes('rei')) && (
                                <div className="mt-6 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowAllPlans((v) => !v)}
                                        className="py-3 px-6 rounded-xl font-bold transition-all bg-white/10 hover:bg-white/20 text-white border border-white/10"
                                    >
                                        {showAllPlans ? 'Ver menos' : 'Ver todos os planos'}
                                    </button>
                                </div>
                            )}

                            <PixDiscordPaymentNotice className="mx-auto mt-10 max-w-2xl" />
                        </>
                    )}


                </div>
            </section>

            {/* Payment Modal */}
            <AnimatePresence>
                {showPaymentModal && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-md"
                            onClick={closePaymentModal}
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none"
                        >
                            <div
                                className="bg-neutral-950 border border-white/10 rounded-3xl w-full max-w-md shadow-2xl pointer-events-auto relative overflow-hidden"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {paymentData && (
                                    <>
                                        <div className="flex items-center justify-between px-6 sm:px-8 py-5 sm:py-6 border-b border-white/5">
                                            <h3 className="text-lg sm:text-xl font-bold text-white">
                                                {paymentData.qrCode ? 'Realizar Pagamento' : 'Confirmar Assinatura'}
                                            </h3>
                                            <button
                                                onClick={closePaymentModal}
                                                className="p-2 hover:bg-white/5 rounded-full transition-colors text-gray-500 hover:text-white"
                                            >
                                                <X size={24} />
                                            </button>
                                        </div>

                                        <div className="p-6 sm:p-8 space-y-6 sm:space-y-8">
                                            <PixDiscordPaymentNotice className="text-left" />
                                            <div className="bg-white/5 rounded-2xl p-4 sm:p-6 border border-white/5">
                                                <div className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">PLANO SELECIONADO</div>
                                                <div className="text-xl sm:text-2xl font-black text-white">{paymentData.plan?.name}</div>
                                                <div className="text-orange-500 text-lg sm:text-xl font-black mt-2">
                                                    R${' '}
                                                    {Number(paymentData.amount ?? paymentData.plan?.pricing ?? 0)
                                                        .toFixed(2)
                                                        .replace('.', ',')}
                                                </div>
                                            </div>

                                            {paymentData.qrCode && (
                                                <div className="space-y-6">
                                                    <div className="bg-white rounded-2xl p-6 flex justify-center shadow-xl shadow-white/5">
                                                        <img
                                                            src={`data:image/png;base64,${paymentData.qrCode}`}
                                                            alt="QR Code PIX"
                                                            className="w-full max-w-[200px]"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2">CHAVE PIX</label>
                                                        <div className="flex gap-2">
                                                            <Input
                                                                type="text"
                                                                value={paymentData.pixKey}
                                                                readOnly
                                                                className="flex-1 rounded-xl py-3 text-xs font-mono"
                                                            />
                                                            <button
                                                                onClick={copyPixKey}
                                                                className="px-4 py-3 bg-orange-500 hover:bg-orange-600 rounded-xl text-white transition-colors"
                                                            >
                                                                <Copy size={20} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="text-center text-gray-500 text-xs">
                                                        Expira em: {formatExpirationDate(paymentData.expiresAt)}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex flex-col gap-3">
                                                {!paymentData.qrCode &&
                                                    (paymentData.checkoutUrl || paymentData.redirectUrl) && (
                                                    <button
                                                        onClick={proceedToPayment}
                                                        className="w-full py-4 bg-orange-500 hover:bg-orange-600 rounded-2xl text-white font-black transition-all"
                                                    >
                                                        IR PARA O PAGAMENTO
                                                    </button>
                                                )}
                                                <button
                                                    onClick={closePaymentModal}
                                                    className="w-full py-4 text-gray-500 hover:text-white font-bold transition-all"
                                                >
                                                    FECHAR
                                                </button>
                                            </div>

                                            {paymentError && (
                                                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-500 text-xs text-center">
                                                    {paymentError}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Gift / Resgatar código Modal */}
            <AnimatePresence>
                {showGiftModal && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-md"
                            onClick={closeGiftModal}
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none"
                        >
                            <div
                                className="bg-neutral-950 border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl pointer-events-auto relative overflow-hidden"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                                    <h3 className="text-lg font-bold text-white">Receber presente</h3>
                                    <button
                                        onClick={closeGiftModal}
                                        className="p-1.5 hover:bg-white/5 rounded-full transition-colors text-gray-500 hover:text-white"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                                <div className="p-5 space-y-4">
                                    <Input
                                        id="gift-code"
                                        type="text"
                                        value={giftCode}
                                        onChange={(e) => { setGiftCode(e.target.value); setGiftRedeemError(''); }}
                                        placeholder="Código"
                                        className="rounded-xl py-3 placeholder:text-white/40"
                                        autoComplete="off"
                                    />
                                    {giftRedeemError && (
                                        <p className="text-red-400 text-sm">{giftRedeemError}</p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleRedeemGiftCode}
                                        disabled={giftRedeemLoading}
                                        className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-70 rounded-xl text-white font-bold transition-all flex items-center justify-center gap-2"
                                    >
                                        {giftRedeemLoading ? (
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            'ATIVAR'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </main>
    );
}
